// Phase 2 — LOC Communication: WhatsApp message templates + deep-link helpers.
// Plain `wa.me` deep links only — no Gallabox / WhatsApp Business API, no
// auto-send. The user always reviews/edits the message before WhatsApp opens.
// NOTE: the word "LOC" is intentionally never used in these customer-facing
// messages — only in the internal formal printed letters.

export const LOC_WHATSAPP_MESSAGE_TYPES = [
  ["first_reminder", "First Reminder"],
  ["second_reminder", "Second Reminder"],
  ["final_reminder", "Final Reminder"],
  ["parking_warning", "Parking Charges Warning"],
  ["parking_started", "Parking Charges Started"],
];

export const LOC_WHATSAPP_MESSAGE_LABELS = Object.fromEntries(LOC_WHATSAPP_MESSAGE_TYPES);

/** Message types that need a "Parking Charge Start Date" filled in. */
export const needsParkingStartDate = (t) => ["second_reminder", "parking_warning", "parking_started"].includes(t);
/** Message types that need a "Last Date Without Parking Charges" filled in. */
export const needsLastDateNoCharge = (t) => t === "parking_warning";

/** Picks a sensible default message type from the case's current stage. */
export const defaultLocWhatsappType = (c) => {
  const stage = c?.stage;
  if (["awaiting_loc1", "loc1_generated", "loc1_sent"].includes(stage)) return "first_reminder";
  if (["loc2_due", "loc2_generated", "loc2_sent"].includes(stage)) return "second_reminder";
  if (["loc3_due", "loc3_generated", "loc3_sent"].includes(stage)) return "final_reminder";
  return "parking_warning";
};

const fmtDMY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

/** Builds the short prefilled WhatsApp text for the given case + message type.
 *  Never includes the full formal LOC letter wording, LOC number, docket
 *  numbers, or internal remarks — only short customer-facing reminder copy. */
export const buildLocWhatsappMessage = (c, messageType, opts = {}) => {
  const name = c?.customer_name || "Customer";
  const model = c?.vehicle_model || "vehicle";
  const reg = c?.registration_number || c?.vin_no || "—";
  const contact = opts.workshopContact || "—";
  const perDay = opts.parkingPerDay ? `₹${opts.parkingPerDay}` : "—";
  const startDate = fmtDMY(opts.parkingStartDate);
  const lastDateNoCharge = fmtDMY(opts.lastDateNoCharge);
  const ready = c?.loc_reason === "vehicle_ready";

  switch (messageType) {
    case "first_reminder":
      return ready
        ? `Dear ${name}, your Yamaha ${model}, Reg. No. ${reg}, is ready for delivery at IVA Motors.\nDespite our earlier follow-ups, the vehicle has not yet been collected.\nKindly arrange pickup at the earliest or contact our Service Team on ${contact}.\nThank you,\nIVA Motors Yamaha`
        : `Dear ${name}, your Yamaha ${model}, Reg. No. ${reg}, is currently at IVA Motors.\nWe are unable to proceed with the required work as your approval/instructions are pending.\nKindly contact our Service Team at the earliest to confirm further action.\nContact: ${contact}\nIVA Motors Yamaha`;
    case "second_reminder":
      return ready
        ? `Dear ${name}, this is a further reminder regarding your Yamaha ${model}, Reg. No. ${reg}, which is still awaiting collection at IVA Motors despite our earlier follow-ups.\nKindly arrange vehicle pickup within the communicated period.\nPlease note that prolonged parking may lead to deterioration of the vehicle, for which IVA Motors cannot be held responsible.\nParking charges of ${perDay} per day will be applicable from ${startDate} if the vehicle is not collected within the permitted period.\nFor assistance, contact: ${contact}\nIVA Motors Yamaha`
        : `Dear ${name}, this is a further reminder regarding your Yamaha ${model}, Reg. No. ${reg}, currently at IVA Motors.\nRequired approval/instructions are still pending and therefore the work cannot proceed.\nKindly contact us immediately to either provide approval for the required work or arrange collection of the vehicle.\nPlease note that prolonged parking may lead to deterioration of the vehicle, for which IVA Motors cannot be held responsible.\nParking charges of ${perDay} per day will be applicable from ${startDate} if the vehicle continues to remain at the workshop.\nContact: ${contact}\nIVA Motors Yamaha`;
    case "final_reminder":
      return ready
        ? `Dear ${name}, despite repeated reminders, your Yamaha ${model}, Reg. No. ${reg}, remains uncollected at IVA Motors.\nParking charges are applicable as previously communicated.\nPlease arrange vehicle collection immediately to avoid further charges and prolonged vehicle deterioration.\nContact: ${contact}\nIVA Motors Yamaha`
        : `Dear ${name}, despite repeated follow-ups, we have not received the required approval/instructions for your Yamaha ${model}, Reg. No. ${reg}.\nThe vehicle continues to remain at IVA Motors and parking charges are applicable as previously communicated.\nPlease contact us immediately to confirm whether the work should proceed or arrange collection of the vehicle.\nContact: ${contact}\nIVA Motors Yamaha`;
    case "parking_warning":
      return `Dear ${name}, your Yamaha ${model}, Reg. No. ${reg}, is still at IVA Motors.\nKindly arrange vehicle pickup by ${lastDateNoCharge}.\nPlease note that prolonged parking may cause deterioration of the vehicle.\nParking charges of ${perDay} per day will become applicable from ${startDate} if the vehicle is not collected.\nFor assistance: ${contact}\nIVA Motors Yamaha`;
    case "parking_started":
    default:
      return `Dear ${name}, your Yamaha ${model}, Reg. No. ${reg}, is still at IVA Motors.\nAs previously communicated, parking charges of ${perDay} per day are now applicable from ${startDate} until vehicle delivery.\nContinued prolonged parking may also lead to deterioration of the vehicle.\nKindly arrange pickup at the earliest.\nContact: ${contact}\nIVA Motors Yamaha`;
  }
};

/** Normalizes a 10-digit Indian mobile number for a `wa.me` link (adds 91). */
export const normalizeMobileForWhatsapp = (mobile) => {
  const digits = (mobile || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

export const buildWhatsappUrl = (mobile, message) => {
  const num = normalizeMobileForWhatsapp(mobile);
  return `https://wa.me/${num}?text=${encodeURIComponent(message || "")}`;
};

